import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Ticket, Check, Copy, Loader2, Download, ExternalLink, Lock,
  AlertTriangle, Sparkles, Mail, KeyRound,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function maskCodeInput(raw) {
  // Strip non-alphanumeric, uppercase. Detect prefix (4 or 5 chars), then split rest in blocks of 4.
  // Known prefixes: DEALF (DealFuel), ASUMO (AppSumo), OTHR (other), DLFY (Dealify),
  // DMRR (DealMirror), PGRD (PitchGround), DIRC (direct). All 4 chars except DEALF/ASUMO/OTHR (5).
  const cleaned = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (cleaned.length === 0) return '';
  const PREFIXES = ['DEALF', 'ASUMO', 'OTHR', 'DLFY', 'DMRR', 'PGRD', 'DIRC'];
  const matched = PREFIXES.find(p => cleaned.startsWith(p));
  const prefixLen = matched ? matched.length : 4;
  const prefix = cleaned.slice(0, prefixLen);
  const rest = cleaned.slice(prefixLen).match(/.{1,4}/g) || [];
  return [prefix, ...rest].join('-');
}

export default function RedeemPage() {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const c = searchParams.get('code');
    if (c) setCode(c.toUpperCase());
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!email.includes('@')) { setError('Please enter a valid email'); return; }
    if (!code || code.length < 10) { setError('Please enter a valid redemption code'); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/redeem`, {
        code: code.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
        password,
      });
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err.message || 'Redemption failed'));
    } finally { setSubmitting(false); }
  };

  const copyKey = () => {
    if (!result?.license_key) return;
    navigator.clipboard?.writeText(result.license_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0E27] via-[#0055FF] to-[#00E5FF] opacity-90"></div>
        <div className="relative max-w-3xl mx-auto px-6 py-12 md:py-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs uppercase tracking-wider font-medium mb-4">
            <Ticket className="w-3.5 h-3.5" /> Lifetime Deal
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-3">Redeem your AEO Orchestra license</h1>
          <p className="text-base md:text-lg opacity-90 max-w-xl mx-auto">
            Enter the redemption code you received from the platform you bought on. We&apos;ll create your account and activate your lifetime access.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        {result ? <SuccessView result={result} onCopy={copyKey} copied={copied} /> : (
          <form onSubmit={submit} className="bg-[#111] border border-white/10 rounded-2xl p-6 md:p-8 space-y-5">
            {error && (
              <div className="flex items-start gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-300">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>{error}</div>
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-wider text-[#A1A1AA] mb-1.5">
                <Ticket className="inline w-3.5 h-3.5 mr-1" />
                Redemption code
              </label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(maskCodeInput(e.target.value))}
                placeholder="DEALF-XXXX-XXXX-XXXX-XXXX"
                autoComplete="off"
                spellCheck={false}
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white text-lg font-mono tracking-wider focus:outline-none focus:border-[#00E5FF]"
              />
              <p className="text-xs text-[#A1A1AA] mt-1.5">Format: prefix + 4 blocks of 4 characters. We&apos;ll auto-uppercase and add dashes.</p>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-[#A1A1AA] mb-1.5">
                <Mail className="inline w-3.5 h-3.5 mr-1" />
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                required
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00E5FF]"
              />
              <p className="text-xs text-[#A1A1AA] mt-1.5">If you already have an account, use the same email and password to stack this code.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#A1A1AA] mb-1.5">
                  <KeyRound className="inline w-3.5 h-3.5 mr-1" />
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="min 8 characters"
                  required
                  minLength={8}
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00E5FF]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#A1A1AA] mb-1.5">Confirm password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00E5FF]"
                />
              </div>
            </div>

            <button type="submit" disabled={submitting}
              className="w-full px-6 py-3.5 bg-[#0055FF] hover:bg-[#3377FF] disabled:opacity-50 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              {submitting ? 'Redeeming…' : 'Redeem & activate'}
            </button>

            <p className="text-xs text-[#A1A1AA] text-center">
              By redeeming you accept the <Link to="/terms" className="underline hover:text-white">Terms</Link> and <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link>.
              Already have an account? <Link to="/login" className="underline text-[#00E5FF]">Log in</Link>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function SuccessView({ result, onCopy, copied }) {
  const unlockDate = result.unlock_at ? new Date(result.unlock_at).toLocaleDateString() : null;
  return (
    <div className="space-y-5">
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6 md:p-8 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <Check className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Welcome, Founder!</h2>
            <p className="text-sm text-[#A1A1AA]">
              {result.is_new_license ? 'Your lifetime license is active.' : 'Codes stacked — your existing license has been upgraded.'}
            </p>
          </div>
        </div>

        <div className="bg-black/40 rounded-lg p-4 border border-white/10">
          <div className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">License key</div>
          <div className="flex items-center gap-3">
            <code className="flex-1 font-mono text-base md:text-lg text-white break-all">{result.license_key}</code>
            <button onClick={onCopy} className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded text-xs flex items-center gap-1">
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="Package" value={result.package_name} />
          <Stat label="Sites" value={result.max_domains} />
          <Stat label="Daily cap" value={`${result.daily_credit_cap.toLocaleString()}`} />
          <Stat label="Credits now" value={`${result.credits_immediate.toLocaleString()}`} />
        </div>

        {result.credits_delayed > 0 && unlockDate && (
          <div className="flex items-start gap-3 px-4 py-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm">
            <Lock className="w-4 h-4 mt-0.5 text-blue-300 flex-shrink-0" />
            <div>
              <strong>{result.credits_delayed.toLocaleString()} additional credits</strong> unlock automatically on <strong>{unlockDate}</strong> (after the platform refund window). You&apos;ll receive an email when they&apos;re available — or unlock them now from your dashboard if you&apos;re ready to forfeit the refund right.
            </div>
          </div>
        )}

        {result.stacking_info && (
          <div className="px-4 py-3 bg-purple-500/10 border border-purple-500/20 rounded-lg text-sm">
            <strong>Stacking applied:</strong> sites went from {result.stacking_info.previous_max_domains} to {result.stacking_info.new_max_domains}
            {result.stacking_info.tier_upgraded && <> · tier upgraded to <strong>{result.package_name}</strong></>}
            {result.stacking_info.clamped && (
              <div className="text-xs text-purple-200 mt-1">Note: your sites were clamped to the lifetime maximum (25). The credit bonus was added in full.</div>
            )}
          </div>
        )}
      </div>

      <div className="bg-[#111] border border-white/10 rounded-2xl p-6 md:p-8 space-y-5">
        <h3 className="text-lg font-bold">Next steps</h3>
        <ol className="space-y-3">
          <Step n={1} title="Download the plugin">
            <a href={result.plugin_download_url} className="text-[#00E5FF] underline inline-flex items-center gap-1">
              <Download className="w-4 h-4" /> Open your dashboard to download
            </a>
          </Step>
          <Step n={2} title="Install on your WordPress site">
            Plugins → Add New → Upload Plugin → choose the ZIP → Install Now → Activate.
          </Step>
          <Step n={3} title="Paste your license key">
            Open the SEO AEO Orchestra menu in your WordPress admin and paste the license key above.
          </Step>
          <Step n={4} title="Done">
            The license auto-locks to your domain on first use. Up to {result.max_domains} site{result.max_domains > 1 ? 's' : ''} included.
          </Step>
        </ol>

        <div className="flex flex-col md:flex-row gap-3">
          <Link to="/dashboard" className="flex-1 px-6 py-3 bg-[#0055FF] hover:bg-[#3377FF] text-white font-semibold rounded-lg text-center inline-flex items-center justify-center gap-2">
            Open dashboard <ExternalLink className="w-4 h-4" />
          </Link>
          <a href={result.plugin_download_url} className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg text-center inline-flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Download plugin
          </a>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-black/30 rounded-lg p-3 border border-white/5">
      <div className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">{label}</div>
      <div className="text-lg font-bold text-white">{value}</div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-3">
      <div className="w-7 h-7 bg-[#0055FF] text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">{n}</div>
      <div>
        <div className="font-semibold text-white">{title}</div>
        <div className="text-sm text-[#A1A1AA]">{children}</div>
      </div>
    </li>
  );
}
